import { orderBy } from 'lodash';

import { ExtendedUser, User } from '../../../../Types';
import {
  AliasContacts,
  ContactDetail,
  ContactDetailWithVertical,
  ExtendedContacts,
  HMDDetails,
  SortColumn,
  SortedResponses,
  countResponses,
} from './types';
import { checkGoUser } from '../../../../Util/User';
import { VerticalOption } from '@amzn/aet-galactus-kit/lib/types';

export const parseIdWithAlias = (
  contacts: ContactDetail[] | null,
  user: User,
  directReport: ExtendedUser[]
): ExtendedContacts[] => {
  if (contacts === null) {
    return [];
  }
  // CSA
  if (directReport.length === 0) {
    return contacts.map(contact => {
      return {
        alias: user.alias,
        ...contact,
      };
    });
  }

  // TM
  return contacts.map(contact => {
    const { userID } = contact;
    const matchedDirectReport = directReport.find(
      element =>
        element.userID === userID.split(':')[1] || element.userID === userID
    );
    return {
      alias: (matchedDirectReport ?? { alias: '' })!.alias,
      ...contact,
    };
  });
};

export const countHMDResponses = (
  yesResponses: ContactDetail[],
  noResponses: ContactDetail[],
  repeatResponses: ContactDetail[]
): countResponses => {
  // inputs can be null for non-real time HMD
  return {
    countYesResponses: yesResponses?.length ?? 0,
    countNoResponses: noResponses?.length ?? 0,
    countRepeatResponses: repeatResponses?.length ?? 0,
  };
};

export const generateContactWithAliasForHmdDetails = (
  yesResponses: ContactDetail[],
  noResponses: ContactDetail[],
  repeatResponses: ContactDetail[],
  user: User,
  directReport: ExtendedUser[]
): AliasContacts => {
  return {
    yesResponsesWithAlias: parseIdWithAlias(yesResponses, user, directReport),
    noResponsesWithAlias: parseIdWithAlias(noResponses, user, directReport),
    repeatResponsesWithAlias: parseIdWithAlias(
      repeatResponses,
      user,
      directReport
    ),
  };
};

export const initialSortForHmdDetails = (
  aliasContactsYes: ExtendedContacts[],
  aliasContactsNo: ExtendedContacts[],
  aliasContactRepeat: ExtendedContacts[]
): SortedResponses => {
  return {
    sortedYesResponses: orderBy(
      aliasContactsYes,
      ['alias', 'issueCode'],
      ['asc', 'asc']
    ),
    sortedNoResponses: orderBy(
      aliasContactsNo,
      ['alias', 'issueCode'],
      ['asc', 'asc']
    ),
    sortedRepeatResponses: orderBy(
      aliasContactRepeat,
      ['alias', 'issueCode'],
      ['asc', 'asc']
    ),
  };
};

export const EmptyHmdDetails = {
  yesResponses: [],
  noResponses: [],
  repeatResponses: [],
  lastAuditDate: '',
};

export const EmptyContactStatus = {
  contactStatuses: [],
};

export const compareTwoArray = (
  arr1: string | null,
  arr2: string | null,
  sortDirection: string
): number => {
  if (arr1 === null && arr2 === null) {
    return 0;
  }
  if (arr1 === null && arr2 !== null) {
    return sortDirection === 'ascending' ? -1 : 1;
  }
  if (arr1 !== null && arr2 === null) {
    return sortDirection === 'ascending' ? 1 : -1;
  }
  if (arr1 !== null && arr2 !== null) {
    const arr1Len = arr1.length;
    const arr2Len = arr2.length;
    let arr1Index = 0;
    let arr2Index = 0;

    while (arr1Index < arr1Len && arr2Index < arr2Len) {
      if (arr1[arr1Index] === arr2[arr2Index]) {
        arr1Index += 1;
        arr2Index += 1;
      } else {
        if (arr1[arr1Index] > arr2[arr2Index])
          return sortDirection === 'ascending' ? 1 : -1;
        if (arr1[arr1Index] < arr2[arr2Index])
          return sortDirection === 'ascending' ? -1 : 1;
      }
    }

    if (arr1Index === arr1Len && arr2Index === arr2Len) return 0;

    return arr1Index === arr1Len && sortDirection === 'ascending' ? -1 : 1;
  }
  return 0;
};

export const customSort = (
  a: ExtendedContacts,
  b: ExtendedContacts,
  sortColumn: keyof SortColumn & string,
  sortDirection: string
): number => {
  if (sortColumn === 'workCategory') {
    return compareTwoArray(a.workCategory, b.workCategory, sortDirection);
  }

  if (a[sortColumn] < b[sortColumn]) {
    return sortDirection === 'ascending' ? -1 : 1;
  }
  if (a[sortColumn] > b[sortColumn]) {
    return sortDirection === 'ascending' ? 1 : -1;
  }

  return 0;
};

// filters out contact details with Consumer or CAP verticalName for non-go users
const filterConsumerAndCAPResponsesOfNonGoUsers = (
  responses: ContactDetailWithVertical[],
  requesterOwner: string
): ContactDetailWithVertical[] => {
  if (!responses) return [];

  if (checkGoUser(requesterOwner)) {
    return responses;
  } else {
    return responses.filter(
      response =>
        !(
          response.verticalName === VerticalOption.Consumer ||
          response.verticalName === VerticalOption.CAP
        )
    );
  }
};

// New function for filtering realtime HMD responses based on org vertical
export const filterRealtimeHMDResponses = (
  responses: ContactDetailWithVertical[],
  requesterOwner: string,
  orgVertical?: string
): ContactDetailWithVertical[] => {
  if (!responses) return [];

  // Skip filtering for GO users
  if (checkGoUser(requesterOwner)) {
    return responses;
  }

  // Only filter if the user's org vertical is Consumer or CAP
  if (
    orgVertical === VerticalOption.Consumer ||
    orgVertical === VerticalOption.CAP
  ) {
    // Filter out all Consumer and CAP contacts
    return responses.filter(
      response =>
        !(
          response.verticalName === VerticalOption.Consumer ||
          response.verticalName === VerticalOption.CAP
        )
    );
  }

  // For all other org verticals, don't filter
  return responses;
};

export const filterRealtimeHMDDetails = (
  hmdDetails: HMDDetails,
  requesterOwner: string,
  orgVertical?: string
): HMDDetails => {
  return {
    yesResponses: filterRealtimeHMDResponses(
      hmdDetails.yesResponses,
      requesterOwner,
      orgVertical
    ),
    noResponses: filterRealtimeHMDResponses(
      hmdDetails.noResponses,
      requesterOwner,
      orgVertical
    ),
    repeatResponses: filterRealtimeHMDResponses(
      hmdDetails.repeatResponses,
      requesterOwner,
      orgVertical
    ),
    lastAuditDate: hmdDetails.lastAuditDate,
  };
};

export const filterHMDDetails = (
  hmdDetails: HMDDetails,
  requesterOwner: string
): HMDDetails => {
  return {
    yesResponses: filterConsumerAndCAPResponsesOfNonGoUsers(
      hmdDetails.yesResponses,
      requesterOwner
    ),
    noResponses: filterConsumerAndCAPResponsesOfNonGoUsers(
      hmdDetails.noResponses,
      requesterOwner
    ),
    repeatResponses: filterConsumerAndCAPResponsesOfNonGoUsers(
      hmdDetails.repeatResponses,
      requesterOwner
    ),
    lastAuditDate: hmdDetails.lastAuditDate,
  };
};
